import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { notificationsService } from '../../services/notificationsService';
import { clientsService } from '../../services/clientsService';
import {
  NotificationResponse,
  NotificationTopic,
  SendNotificationRequest
} from '../../types/notifications';
import Modal from '../modals/Modal';
import ImageViewerModal from '../modals/ImageViewerModal';
import './Page.css';

const PAGE_SIZE = 20;

const Notifications: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [notifications, setNotifications] = useState<NotificationResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [viewerImage, setViewerImage] = useState<{ url: string; title?: string } | null>(null);
  const [filtersModalNotification, setFiltersModalNotification] = useState<NotificationResponse | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');
  const [duplicatingNotification, setDuplicatingNotification] = useState<NotificationResponse | null>(null);

  const [topic, setTopic] = useState<NotificationTopic>('FCM_TOPIC_CLIENTS');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [imageUrl, setImageUrl] = useState('');

  const currentPage = parseInt(searchParams.get('page') || '0', 10);

  const updatePage = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  };

  const topicOptions = useMemo(() => (
    [
      { value: 'FCM_TOPIC_CLIENTS', label: t('notifications.topics.clients') },
      { value: 'FCM_TOPIC_CLIENTS_ANDROID', label: t('notifications.topics.clientsAndroid') },
      { value: 'FCM_TOPIC_CLIENTS_IOS', label: t('notifications.topics.clientsIos') },
      { value: 'TEST_CLIENT_ACCOUNT', label: t('notifications.topics.testAccount') },
      { value: 'SPECIFIC_CLIENTS', label: t('notifications.topics.specificClients') },
    ] as { value: NotificationTopic; label: string }[]
  ), [t]);

  useEffect(() => {
    fetchNotifications();
  }, [currentPage]);

  const fetchNotifications = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await notificationsService.getNotifications({
        page: currentPage,
        pageSize: PAGE_SIZE,
      });

      if (response.success && response.data) {
        setNotifications(response.data.notifications);
        setTotalCount(response.data.totalCount);
      } else {
        setError(response.error || t('notifications.fetchFailed'));
      }
    } catch (error) {
      setError(t('notifications.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setDuplicatingNotification(null);
    setTopic('FCM_TOPIC_CLIENTS');
    setTitle('');
    setMessage('');
    setImageUrl('');
    setModalError('');
    setIsModalOpen(true);
  };

  const openDuplicateModal = (notification: NotificationResponse) => {
    setDuplicatingNotification(notification);
    setTopic(notification.clientsFilter ? 'SPECIFIC_CLIENTS' : notification.topic);
    setTitle(notification.title);
    setMessage(notification.message);
    setImageUrl(notification.imageUrl || '');
    setModalError('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (modalLoading) return;
    setIsModalOpen(false);
    setDuplicatingNotification(null);
    setModalError('');
  };

  const handleSend = async (event: React.FormEvent) => {
    event.preventDefault();
    setModalLoading(true);
    setModalError('');
    setError('');
    setSuccessMessage('');

    try {
      let response;
      if (topic === 'SPECIFIC_CLIENTS' && !duplicatingNotification?.clientsFilter) {
        setModalError(t('notifications.specificClientsUnavailable'));
        setModalLoading(false);
        return;
      }
      if (duplicatingNotification?.clientsFilter) {
        response = await clientsService.sendClientsNotification({
          topic: 'SPECIFIC_CLIENTS',
          title: title.trim(),
          message: message.trim(),
          imageUrl: imageUrl.trim() ? imageUrl.trim() : null,
          clientsFilter: duplicatingNotification.clientsFilter
        });
      } else {
        const payload: SendNotificationRequest = {
          topic,
          title: title.trim(),
          message: message.trim(),
          imageUrl: imageUrl.trim() ? imageUrl.trim() : null,
        };
        response = await notificationsService.sendNotification(payload);
      }

      if (response.success) {
        setIsModalOpen(false);
        setSuccessMessage(t('notifications.sendSuccess'));
        if (currentPage !== 0) {
          updatePage(0);
        } else {
          await fetchNotifications();
        }
      } else {
        setModalError(response.error || t('notifications.sendFailed'));
      }
    } catch (error) {
      setModalError(t('notifications.sendFailed'));
    } finally {
      setModalLoading(false);
    }
  };

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTopicLabel = (value: NotificationTopic) => {
    return topicOptions.find((option) => option.value === value)?.label || value;
  };

  const renderImageLink = (url: string | null) => {
    if (!url) {
      return <span style={{ color: '#999' }}>{t('notifications.noImage')}</span>;
    }

    return (
      <button
        type="button"
        onClick={() => setViewerImage({ url, title: t('notifications.viewImage') })}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          color: '#007bff',
          cursor: 'pointer',
          textDecoration: 'underline'
        }}
      >
        {t('notifications.viewImage')}
      </button>
    );
  };

  const renderProgress = (value?: number | null) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return <span style={{ color: '#999' }}>—</span>;
    }

    const clamped = Math.max(0, Math.min(100, value));
    const label = Number.isInteger(clamped) ? `${clamped}` : clamped.toFixed(1);

    return (
      <div style={{ minWidth: '120px' }}>
        <div
          style={{
            height: '8px',
            backgroundColor: '#e9ecef',
            borderRadius: '999px',
            overflow: 'hidden',
            marginBottom: '6px'
          }}
        >
          <div
            style={{
              width: `${clamped}%`,
              height: '100%',
              backgroundColor: clamped >= 100 ? '#28a745' : '#007bff'
            }}
          />
        </div>
        <div style={{ fontSize: '0.8rem', color: '#555' }}>{label}%</div>
      </div>
    );
  };

  return (
    <div className="page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1>{t('notifications.title')}</h1>
          <p>{t('notifications.subtitle')}</p>
        </div>
        <button
          onClick={openCreateModal}
          className="search-button"
          style={{ whiteSpace: 'nowrap' }}
        >
          {t('notifications.sendNew')}
        </button>
      </div>

      <div className="page-content">
        {!loading && notifications.length > 0 && (
          <div style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <span style={{ fontSize: '0.9rem', color: '#666' }}>{t('notifications.totalNotifications')}</span>
              <span style={{ fontSize: '1.5rem', fontWeight: 'bold', marginLeft: '0.5rem' }}>{totalCount}</span>
            </div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              {t('common.page')} {currentPage + 1} {t('common.of')} {Math.ceil(totalCount / PAGE_SIZE)}
            </div>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}
        {successMessage && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            color: '#155724',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            marginBottom: '1.5rem'
          }}>
            {successMessage}
          </div>
        )}

        <div className="table-container">
          {loading ? (
            <div className="loading-container">{t('notifications.loading')}</div>
          ) : notifications.length === 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('notifications.topic')}</th>
                  <th>{t('notifications.titleLabel')}</th>
                  <th>{t('notifications.message')}</th>
                  <th>{t('notifications.imageUrl')}</th>
                  <th>{t('notifications.progress')}</th>
                  <th>{t('notifications.createdAt')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={7} className="no-data">
                    {t('notifications.noNotifications')}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('notifications.topic')}</th>
                  <th>{t('notifications.titleLabel')}</th>
                  <th>{t('notifications.message')}</th>
                  <th>{t('notifications.imageUrl')}</th>
                  <th>{t('notifications.progress')}</th>
                  <th>{t('notifications.createdAt')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map((notification) => (
                  <tr key={notification.id}>
                    <td>{getTopicLabel(notification.topic)}</td>
                    <td style={{ fontWeight: 600 }}>{notification.title}</td>
                    <td>
                      <div style={{ maxWidth: '320px', color: '#555' }}>
                        {notification.message.length > 120
                          ? notification.message.substring(0, 120) + '...'
                          : notification.message}
                      </div>
                    </td>
                    <td>{renderImageLink(notification.imageUrl)}</td>
                    <td>{renderProgress(notification.progress)}</td>
                    <td>{formatDate(notification.createdAt)}</td>
                    <td>
                      {notification.clientsFilter && (
                        <button
                          onClick={() => setFiltersModalNotification(notification)}
                          style={{
                            padding: '0.5rem 0.9rem',
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            marginRight: '0.5rem'
                          }}
                        >
                          {t('notifications.showFilters')}
                        </button>
                      )}
                      <button
                        onClick={() => openDuplicateModal(notification)}
                        style={{
                          padding: '0.5rem 0.9rem',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        {t('notifications.duplicate')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && notifications.length > 0 && (
          <div className="pagination">
            <button
              className="pagination-button"
              onClick={() => updatePage(currentPage - 1)}
              disabled={currentPage === 0}
            >
              {t('common.previous')}
            </button>
            <span className="pagination-info">{t('common.page')} {currentPage + 1}</span>
            <button
              className="pagination-button"
              onClick={() => updatePage(currentPage + 1)}
              disabled={notifications.length < PAGE_SIZE}
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={duplicatingNotification ? t('notifications.duplicateTitle') : t('notifications.composeTitle')}
        maxWidth="600px"
      >
        {modalError && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>
            {modalError}
          </div>
        )}
        <form className="modal-form" onSubmit={handleSend}>
          <div className="modal-form-group">
            <label>{t('notifications.topic')} *</label>
            <select
              value={topic}
              onChange={(e) => setTopic(e.target.value as NotificationTopic)}
              disabled={modalLoading || !!duplicatingNotification?.clientsFilter}
              required
            >
              {topicOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {duplicatingNotification?.clientsFilter && (
              <small style={{ color: '#666' }}>{t('notifications.topicLocked')}</small>
            )}
          </div>
          <div className="modal-form-group">
            <label>{t('notifications.titleLabel')} *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('notifications.titlePlaceholder')}
              disabled={modalLoading}
              required
            />
          </div>
          <div className="modal-form-group">
            <label>{t('notifications.message')} *</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('notifications.messagePlaceholder')}
              disabled={modalLoading}
              required
            />
          </div>
          <div className="modal-form-group">
            <label>{t('notifications.imageUrl')}</label>
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder={t('notifications.imageUrlPlaceholder')}
              disabled={modalLoading}
            />
            <small style={{ color: '#666' }}>{t('notifications.imageUrlHint')}</small>
            {imageUrl.trim() && (
              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                  {t('notifications.preview')}
                </div>
                <img
                  src={imageUrl.trim()}
                  alt={t('notifications.previewAlt')}
                  style={{
                    width: '100%',
                    maxHeight: '240px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef'
                  }}
                />
              </div>
            )}
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="modal-button secondary"
              onClick={closeModal}
              disabled={modalLoading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="modal-button primary"
              disabled={modalLoading}
            >
              {modalLoading ? t('notifications.sending') : t('notifications.send')}
            </button>
          </div>
        </form>
      </Modal>
      <ImageViewerModal
        isOpen={!!viewerImage}
        onClose={() => setViewerImage(null)}
        imageUrl={viewerImage?.url || null}
        imageTitle={viewerImage?.title}
      />
      <Modal
        isOpen={!!filtersModalNotification}
        onClose={() => setFiltersModalNotification(null)}
        title={t('notifications.filtersTitle')}
        maxWidth="700px"
      >
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
          {filtersModalNotification?.clientsFilter
            ? JSON.stringify(filtersModalNotification.clientsFilter, null, 2)
            : ''}
        </pre>
        <div className="modal-actions" style={{ marginTop: '1rem' }}>
          <button
            type="button"
            className="modal-button secondary"
            onClick={() => setFiltersModalNotification(null)}
          >
            {t('common.close')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Notifications;
